import { DEFAULT_OPTIONS } from '../../../../FlowchartView/TaskNode/DecisionOptionsModal';
import {
  toConditionEntry,
  isV2Format,
  isV1Format,
  getDependencyTaskId,
  getDependencyBranch,
} from './formats';

/**
 * Convert v1 flat array to v2 block format.
 * Each OR-group in v1 becomes a block with innerOperator: 'AND'.
 * Blocks are joined by outerOperator: 'OR'.
 */
export function convertV1ToV2(v1Conditions) {
  if (!v1Conditions || !Array.isArray(v1Conditions) || v1Conditions.length === 0) {
    return { version: 2, blocks: [] };
  }

  const blocks = [];
  let currentBlock = { conditions: [], innerOperator: 'AND', outerOperator: null };

  for (let i = 0; i < v1Conditions.length; i++) {
    const condition = v1Conditions[i];
    if (!condition.taskId) continue;

    const entry = toConditionEntry(condition);

    if (i === 0 || condition.operator === 'AND') {
      currentBlock.conditions.push(entry);
    } else if (condition.operator === 'OR') {
      if (currentBlock.conditions.length > 0) {
        blocks.push(currentBlock);
      }
      currentBlock = { conditions: [entry], innerOperator: 'AND', outerOperator: 'OR' };
    }
  }

  if (currentBlock.conditions.length > 0) {
    blocks.push(currentBlock);
  }

  if (blocks.length > 0) {
    blocks[0].outerOperator = null;
  }

  return { version: 2, blocks };
}

/**
 * Convert old dependsOn array to v2 format.
 * dependsOn is implicitly AND — becomes a single block.
 */
export function convertDependsOnToV2(dependsOn) {
  if (!dependsOn || !Array.isArray(dependsOn) || dependsOn.length === 0) {
    return { version: 2, blocks: [] };
  }

  const conditions = [];
  for (const dep of dependsOn) {
    const taskId = getDependencyTaskId(dep);
    const branch = getDependencyBranch(dep);
    if (!taskId) continue;
    conditions.push(toConditionEntry({
      taskId,
      branch,
      waitSeconds: typeof dep === 'object' ? dep.waitSeconds : null,
      waitMinutes: typeof dep === 'object' ? dep.waitMinutes : null,
    }));
  }

  if (conditions.length === 0) {
    return { version: 2, blocks: [] };
  }

  return {
    version: 2,
    blocks: [{ conditions, innerOperator: 'AND', outerOperator: null }],
  };
}

/**
 * Normalize any unlockConditions value to v2 format.
 * Handles: v2 object, v1 array, null/undefined.
 */
export function normalizeToV2(unlockConditions) {
  if (isV2Format(unlockConditions)) return unlockConditions;
  if (isV1Format(unlockConditions)) return convertV1ToV2(unlockConditions);
  return { version: 2, blocks: [] };
}

/**
 * Get effective conditions in v2 format for a task.
 * Checks unlockConditions first (v1 or v2), then falls back to dependsOn.
 */
export function getEffectiveConditionsV2(task) {
  if (task.unlockConditions) {
    if (isV2Format(task.unlockConditions)) return task.unlockConditions;
    if (isV1Format(task.unlockConditions) && task.unlockConditions.length > 0) {
      return convertV1ToV2(task.unlockConditions);
    }
  }
  return convertDependsOnToV2(task.dependsOn);
}

function resolveBranchFromDepTask(depTask, branchRaw) {
  if (branchRaw === undefined || branchRaw === null || branchRaw === '') return null;
  if (typeof branchRaw === 'string') return branchRaw;
  if (typeof branchRaw === 'number' && depTask?.nodeType === 'decision') {
    const opts = depTask.options?.length > 0 ? depTask.options : DEFAULT_OPTIONS;
    return opts[branchRaw]?.id ?? null;
  }
  return null;
}

/**
 * Clipboard-shaped conditions (fokus v2 JSON): { task: <index> } without taskId.
 * When data was never run through paste remap, resolve index against the parent task list.
 * @param {object} cond - v1/v2 condition; may use taskId (app) or task (clipboard index)
 * @param {Array<{id: string}>|null} indexParentTasks - sprint tasks in array order for index resolution
 */
export function resolveConditionToEntry(cond, indexParentTasks) {
  if (cond.taskId) {
    return toConditionEntry(cond);
  }
  if (indexParentTasks != null && typeof cond.task === 'number') {
    const depTask = indexParentTasks[cond.task];
    if (!depTask?.id) return null;
    const branch = resolveBranchFromDepTask(depTask, cond.branch);
    return toConditionEntry({ taskId: depTask.id, branch: branch ?? null, waitMinutes: cond.waitMinutes });
  }
  return null;
}
